/**
 * TestRunModal — Modal für Test-Step + Test-Pipeline.
 * @since v2.514.0 (#1173)
 *
 * Modes:
 *   - 'step': Test eines einzelnen Steps (POST /playbooks/:id/test-step/:stepId)
 *   - 'pipeline': Sandbox-Run des kompletten Playbooks (POST /playbooks/:id/test-pipeline)
 */
import { useState } from 'react';
import { Modal, Button } from '../ui';

const API = import.meta.env.VITE_API_URL || '';

const DEFAULT_PAYLOAD = JSON.stringify({
  case_id: 99000000,
  odoo_case_id: 99000000,
  title: 'Test-Case (Builder)',
  description: 'Sandbox-Run aus UI-Builder',
}, null, 2);

export default function TestRunModal({ open, onClose, playbookId, mode = 'step', stepId, defaultEventType = 'case_stage_changed' }) {
  const [payload, setPayload] = useState(DEFAULT_PAYLOAD);
  const [priorOutputs, setPriorOutputs] = useState('{}');
  const [eventType, setEventType] = useState(defaultEventType);
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState(null);

  async function run() {
    setRunning(true); setErr(null); setResult(null);
    try {
      const body = JSON.parse(payload || '{}');
      const url = mode === 'pipeline'
        ? `${API}/api/flowai/playbooks/${playbookId}/test-pipeline`
        : `${API}/api/flowai/playbooks/${playbookId}/test-step/${stepId}`;
      const req = mode === 'pipeline'
        ? { event_type: eventType, event_payload: body }
        : { event_payload: body, prior_step_outputs: JSON.parse(priorOutputs || '{}') };
      const r = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      });
      const d = await r.json();
      if (!r.ok) {
        setErr(d.error || `HTTP ${r.status}`);
      } else {
        setResult(d);
      }
    } catch (e) {
      setErr(String(e));
    } finally {
      setRunning(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'pipeline' ? 'Pipeline-Test (Sandbox)' : `Step-Test: ${stepId || '—'}`}
      size="xl"
      footer={
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onClose}>Schließen</Button>
          <Button onClick={run} disabled={running}>{running ? 'Läuft…' : 'Ausführen'}</Button>
        </div>
      }
    >
      <div className="space-y-4 text-sm">
        {mode === 'pipeline' && (
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] font-medium">Event-Type</label>
            <input
              value={eventType}
              onChange={(e) => setEventType(e.target.value)}
              className="w-full mt-1 px-2 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-xs font-mono"
            />
          </div>
        )}
        <div>
          <label className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] font-medium">Event-Payload (JSON)</label>
          <textarea
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            rows={8}
            className="w-full mt-1 px-2 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-xs font-mono"
          />
        </div>
        {mode === 'step' && (
          <div>
            <label className="text-[10px] uppercase tracking-wide text-[var(--text-tertiary)] font-medium">Prior-Step-Outputs (JSON, optional)</label>
            <textarea
              value={priorOutputs}
              onChange={(e) => setPriorOutputs(e.target.value)}
              rows={4}
              placeholder='{ "step1": { "key": "value" } }'
              className="w-full mt-1 px-2 py-1.5 border border-[var(--border-subtle,#E5E7EB)] rounded text-xs font-mono"
            />
          </div>
        )}
        {err && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded px-3 py-2 text-xs">
            <div className="font-medium mb-1">Fehler</div>
            <pre className="whitespace-pre-wrap font-mono text-[11px]">{err}</pre>
          </div>
        )}
        {result && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-3 py-2">
            <div className="font-medium mb-1 text-xs">Ergebnis</div>
            <pre className="whitespace-pre-wrap font-mono text-[11px] max-h-[360px] overflow-y-auto">{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}
      </div>
    </Modal>
  );
}
